import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { IconButton, Stack, Typography } from '@mui/material';
import { ProductGroups } from 'enums';
import { NodeVariants } from 'features/co-pilot/co-pilot.types';
import { LinkedinAccount } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import _ from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Node } from 'reactflow';
import useCampaignNodeStore from 'stores/campaign-node-store';
import { makeStyles } from 'tss-react/mui';
import NodeItem from './node-item';
import UnableToGenerateMessageDialog from './unable-to-generate-contact-request-dialog';

interface Props {
  productGroup: ProductGroups;
  campaignId: string;
  nodes: Node[];
  loading: boolean;

  linkedinAccount: LinkedinAccount | undefined;
  customPlaceholders: import('../../campaign-builder.types').CustomPlaceholder[];
  fieldInUse: { [key: string]: boolean };

  handleSelectNodeFromList(node: Node): () => void;

  handleUnselectNodeFromList(): void;
}

const AiGeneratedMessaging: React.FC<Props> = ({
  productGroup,
  campaignId,
  nodes,
  loading,
  linkedinAccount,
  customPlaceholders,
  fieldInUse,
  handleSelectNodeFromList,
  handleUnselectNodeFromList,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { selectedNode, setSelectedNode } = useCampaignNodeStore((state) => ({
    selectedNode: state.selectedNode as Node | null,
    setSelectedNode: state.setSelectedNode,
  }));

  const [unableToGenerateContactRequestDialog, setUnableToGenerateContactRequestDialog] = useState<{ open: boolean }>({ open: false });

  const messageNodes = nodes
    .filter((node) => node.type === NodeVariants.FUNCTION_NODE && node.data.functionName)
    .sort((a, b) => {
      // Handling cases where functionNumber is undefined
      const numA = a.data.functionNumber ?? Number.POSITIVE_INFINITY; // undefined becomes a very high number
      const numB = b.data.functionNumber ?? Number.POSITIVE_INFINITY; // undefined becomes a very high number

      return numA - numB;
    });
  const selectedNodeIndex = messageNodes.findIndex((node) => node.id === selectedNode?.id);

  const handleSelectNextNode = () => {
    const nextNode = selectedNodeIndex < messageNodes.length - 1 ? messageNodes[selectedNodeIndex + 1] : messageNodes[0];
    setSelectedNode(nextNode);
  };

  const handleSelectPrevNode = () => {
    const prevNode = selectedNodeIndex > 0 ? messageNodes[selectedNodeIndex - 1] : messageNodes[messageNodes.length - 1];
    setSelectedNode(prevNode);
  };

  /**
   * Unable to generate contact request dialog handlers
   */

  const handleOpenUnableToGenerateContactRequestDialog = () => {
    setUnableToGenerateContactRequestDialog((prev) => ({ ...prev, open: true }));
  };

  const handleCloseUnableToGenerateContactRequestDialog = () => {
    setUnableToGenerateContactRequestDialog({ open: false });
  };

  return (
    <div className={classes.aiGeneratedMessaging}>
      {_.isEmpty(messageNodes) ? (
        <Stack justifyContent={'center'} alignItems={'center'} pt={'16px'} sx={{ height: '100%' }}>
          <Typography className={classes.greyText} align={'center'}>
            {t('noMessageNodes')}
          </Typography>
        </Stack>
      ) : (
        <Stack gap={2}>
          <>
            <Typography className={classes.formTitle}>{t('usePromptGenerator')}</Typography>
            {selectedNode?.type === NodeVariants.FUNCTION_NODE ? (
              <Stack direction={'row'} alignItems={'center'} gap={2}>
                <IconButton size={'small'} onClick={handleSelectPrevNode}>
                  <ArrowBackIcon className={classes.arrowIcon} />
                </IconButton>
                <Stack direction={'row'} alignItems={'center'} gap={1}>
                  <Typography className={classes.formRegularText}>{`${t('message')} ${selectedNodeIndex + 1} / ${messageNodes.length}`}</Typography>
                  <Typography className={classes.formTitle}>|</Typography>
                  <Typography className={classes.formTitle}>{t(selectedNode.data.functionName)}</Typography>
                </Stack>
                <IconButton size={'small'} onClick={handleSelectNextNode}>
                  <ArrowForwardIcon className={classes.arrowIcon} />
                </IconButton>
              </Stack>
            ) : null}
            <Stack gap={1}>
              {messageNodes.map((node) => (
                <NodeItem
                  key={node.id}
                  productGroup={productGroup}
                  node={node}
                  campaignId={campaignId}
                  loading={loading}
                  linkedinAccount={linkedinAccount}
                  customPlaceholders={customPlaceholders}
                  fieldInUse={fieldInUse}
                  handleSelectNodeFromList={handleSelectNodeFromList(node)}
                  handleUnselectNodeFromList={handleUnselectNodeFromList}
                  handleOpenUnableToGenerateContactRequestDialog={handleOpenUnableToGenerateContactRequestDialog}
                />
              ))}
            </Stack>
          </>
        </Stack>
      )}

      <UnableToGenerateMessageDialog
        open={unableToGenerateContactRequestDialog.open}
        handleClose={handleCloseUnableToGenerateContactRequestDialog}
      />
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  aiGeneratedMessaging: {
    paddingBottom: '32px',
    marginBottom: '16px',
    borderBottom: `1px solid ${theme.palette.greys.light}`,
  },
  greyText: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.greys.medium,
    fontWeight: 'bold',
    padding: '0 80px',
  },
  formRegularText: {
    fontSize: theme.typography.pxToRem(14),
  },
  formTitle: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 'bold',
  },
  arrowIcon: {
    fontSize: theme.typography.pxToRem(16),
    color: theme.palette.text.primary,
  },
}));

export default AiGeneratedMessaging;
